import React, { useState, useEffect, useRef } from 'react';
import { Star, Trophy, Sparkles, Palette, Clock, Gift, ArrowRight } from 'lucide-react';
import { DotLottie } from '@lottiefiles/dotlottie-web';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import PublicUploadTree from './PublicUploadTree';
import WhyParticipate from './WhyParticipate';

export default function Hero() {
  const [showUpload, setShowUpload] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lottieRef = useRef<DotLottie | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleParticipate = () => {
    setShowUpload(true);
  };

  useEffect(() => {
    if (canvasRef.current && 'IntersectionObserver' in window) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !lottieRef.current) {
              lottieRef.current = new DotLottie({
                autoplay: true,
                loop: true,
                canvas: canvasRef.current!,
                src: 'https://lottie.host/25d40713-5a01-4466-b522-624786490296/IjJHeURKbN.lottie',
                renderer: 'canvas',
                rendererSettings: {
                  preserveAspectRatio: 'xMidYMid meet',
                  clearCanvas: true,
                  progressiveLoad: true,
                  hideOnTransparent: true
                }
              });
            } else if (!entry.isIntersecting && lottieRef.current) {
              lottieRef.current.pause();
            } else if (entry.isIntersecting && lottieRef.current) {
              lottieRef.current.play();
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '50px'
        }
      );

      observerRef.current.observe(canvasRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (lottieRef.current) {
        lottieRef.current.destroy();
        lottieRef.current = null;
      }
    };
  }, []);

  const steps = [
    {
      icon: Gift,
      title: "Decora tu Árbol",
      description: "Crea una decoración única y especial"
    },
    {
      icon: Star,
      title: "Sube tu Foto",
      description: "Comparte tu creación antes del 25 de diciembre"
    },
    {
      icon: Trophy,
      title: "Gana Premios",
      description: "Consigue votos y llévate increíbles regalos"
    }
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-primary via-primary to-primary-dark text-white overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxIDAgNiAyLjY5IDYgNnMtMi42OSA2LTYgNi02LTIuNjktNi02IDIuNjktNiA2LTZ6bTEyIDI0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnptLTI0IDAtNiA2djZoNnY2aDZ2LTZoNnYtNmgtNnYtNmgtNnY2aC02eiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utb3BhY2l0eT0iLjEiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-10" />

      <div className="container mx-auto px-4 pt-32 pb-20 relative">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center items-center flex-col mb-12">
            <div className="relative mb-6">
              <canvas 
                ref={canvasRef}
                className="w-[200px] h-[200px] will-change-transform"
                width="200"
                height="200"
                style={{
                  transform: 'translate3d(0,0,0)',
                  backfaceVisibility: 'hidden'
                }}
              />
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-secondary/20 rounded-full animate-pulse" />
            </div>
            
            <Logo size="lg" className="mb-6" />
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 font-serif">
              <span className="text-secondary">Navidad</span> 2024
            </h1>

            <div className="flex items-center justify-center space-x-4 mb-8">
              <Link 
                to="/reglas"
                className="flex items-center bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition-colors"
              >
                <Clock className="h-5 w-5 text-secondary mr-2" />
                <span className="text-sm">Hasta el 25 de diciembre</span>
              </Link>
              <Link 
                to="/premios"
                className="flex items-center bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition-colors"
              >
                <Trophy className="h-5 w-5 text-secondary mr-2" />
                <span className="text-sm">Premios Increíbles</span>
              </Link>
            </div>

            <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl mx-auto leading-relaxed">
              ¡Participa en el mejor concurso de árboles de Navidad! Comparte tu creatividad, 
              consigue votos y gana premios espectaculares.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <button
                onClick={handleParticipate}
                className="bg-secondary hover:bg-secondary/90 text-white px-8 py-4 rounded-full text-xl font-bold transition-all transform hover:scale-105 shadow-[0_0_20px_rgba(255,78,80,0.3)] hover:shadow-[0_0_30px_rgba(255,78,80,0.5)] border-2 border-white/20 flex items-center"
              >
                ¡Participar Ahora! <ArrowRight className="ml-2 h-6 w-6" />
              </button>

              <a 
                href="https://t.me/ofertium_es" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-[#0088cc] hover:bg-[#0077b5] text-white px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 shadow-[0_0_20px_rgba(0,136,204,0.3)] hover:shadow-[0_0_30px_rgba(0,136,204,0.5)] flex items-center"
              >
                <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                Únete a Telegram
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={index}
                  className="relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-sm p-8 group hover:bg-white/15 transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-yellow-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Icon className="h-12 w-12 text-yellow-400 mx-auto mb-4 relative z-10" />
                  <h3 className="text-2xl font-bold mb-3 relative z-10">{step.title}</h3>
                  <p className="text-gray-200 relative z-10">{step.description}</p>
                </div>
              );
            })}
          </div>

          <WhyParticipate />
        </div>
      </div>

      {showUpload && <PublicUploadTree onClose={() => setShowUpload(false)} />}
    </div>
  );
}